import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Form from "antd/es/form";
import Rate from "antd/es/rate";
import { defineMessages, useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Popconfirm, TextField } from "@mapmycustomers/ui";

import MoreButton from "@app/component/MoreButton";
import { getFunnels } from "@app/store/deal";
import { getFavoriteFunnels } from "@app/store/iam";
import { toggleFavoriteFunnel } from "@app/store/iam/actions";
import { getLayoutsForEntity } from "@app/store/layout";
import { RootState } from "@app/store/rootReducer";

import { MAX_FAVORITE_FUNNELS } from "../../util/consts";

import styles from "./FunnelHeader.module.scss";

const messages = defineMessages({
  bookmarksLimitTooltip: {
    id: "deal.funnel.settings.header.bookmark.tooltip.limitExceed",
    defaultMessage: "Only {maxFunnelCount} funnels can be starred at once",
    description: "bookmark tooltip text if limit is exhausted",
  },
  bookmarkTooltip: {
    id: "deal.funnel.settings.header.bookmark.tooltip",
    defaultMessage: "Star up to three funnels to pin them to the top of a funnel select dropdown",
    description: "bookmark tooltip text",
  },
  deleteFunnelConfirmationCancel: {
    id: "deals.funnel.deleteButton.no",
    defaultMessage: "No",
    description: "No button for Delete Funnel Pop Confirm",
  },
  deleteFunnelConfirmationOk: {
    id: "deals.funnel.deleteButton.yes",
    defaultMessage: "Yes",
    description: "Yes button for Delete Funnel Pop Confirm",
  },
  deleteFunnelConfirmationTitle: {
    id: "deals.funnel.deleteButton.title",
    defaultMessage: "This funnel will be deleted from the Org",
    description: "Title for Delete Funnel Pop Confirm",
  },
  deleteMenuItem: {
    id: "deal.funnel.settings.header.menu.delete",
    defaultMessage: "Delete Funnel",
    description: "Delete funnel name menu item in Manage Funnel modal",
  },
  editMenuItem: {
    id: "deal.funnel.settings.header.menu.edit",
    defaultMessage: "Edit Funnel Name",
    description: "Edit funnel name menu item in Manage Funnel modal",
  },
});

interface OwnProps {
  editing: boolean;
  funnel: Funnel;
  onDelete?: (funnel: Funnel) => void;
  onToggleEditing: (editing: boolean) => void;
}

interface Props extends OwnProps {
  favoriteFunnelsIds: Funnel["id"][];
  isFunnelUsedInLayouts: boolean;
  isLastFunnel: boolean;
  reachedFavoriteFunnelsLimit: boolean;
  toggleFavoriteFunnel: typeof toggleFavoriteFunnel.request;
}

const FunnelHeader: React.FC<Props> = ({
  editing,
  favoriteFunnelsIds,
  funnel,
  isFunnelUsedInLayouts,
  isLastFunnel,
  onDelete,
  onToggleEditing,
  reachedFavoriteFunnelsLimit,
  toggleFavoriteFunnel,
}) => {
  const intl = useIntl();
  const handleFavoriteChange = useCallback(() => {
    toggleFavoriteFunnel({ funnelId: funnel.id });
  }, [funnel, toggleFavoriteFunnel]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onToggleEditing(false);
      }
      if (event.key === "Enter") {
        event.stopPropagation();
      }
    },
    [onToggleEditing]
  );

  const canDelete = !isLastFunnel && !isFunnelUsedInLayouts;
  const [deleteConfirmationVisible, showDeleteConfirmation, hideDeleteConfirmation] = useBoolean();

  const menuProps = useMemo(() => {
    return {
      items: [
        {
          key: "edit",
          label: intl.formatMessage(messages.editMenuItem),
          onClick: () => onToggleEditing(true),
        },
        {
          danger: true,
          disabled: !canDelete,
          key: "delete",
          label: intl.formatMessage(messages.deleteMenuItem),
          onClick: showDeleteConfirmation,
        },
      ],
    };
  }, [onToggleEditing, canDelete, showDeleteConfirmation, intl]);

  const handleDelete = useCallback(() => {
    hideDeleteConfirmation();
    onDelete?.(funnel);
  }, [hideDeleteConfirmation, funnel, onDelete]);

  return (
    <div className={styles.content}>
      <div>
        <Popconfirm
          cancelText={intl.formatMessage(messages.deleteFunnelConfirmationCancel)}
          description={intl.formatMessage(messages.deleteFunnelConfirmationTitle)}
          okText={intl.formatMessage(messages.deleteFunnelConfirmationOk)}
          onCancel={hideDeleteConfirmation}
          onConfirm={handleDelete}
          open={deleteConfirmationVisible}
          placement="left"
        >
          <Dropdown menu={menuProps} trigger={["click"]}>
            <MoreButton type="link" />
          </Dropdown>
        </Popconfirm>
      </div>
      <div className={styles.innerContent}>
        <h3 className={styles.funnelTitle} onClick={stopEvents}>
          <span className={styles.funnelHeaderDisplay}>
            {editing ? (
              <Form.Item className={styles.nameInput} name="name">
                <TextField onKeyDown={handleKeyDown} size="middle" />
              </Form.Item>
            ) : (
              <div className={styles.nameTruncate}>{funnel.name}</div>
            )}
            <Rate
              className={styles.headerStarIcon}
              count={1}
              disabled={reachedFavoriteFunnelsLimit && !favoriteFunnelsIds.includes(funnel.id)}
              onChange={handleFavoriteChange}
              tooltips={[
                favoriteFunnelsIds.length < MAX_FAVORITE_FUNNELS
                  ? intl.formatMessage(messages.bookmarkTooltip)
                  : intl.formatMessage(messages.bookmarksLimitTooltip, {
                      maxFunnelCount: MAX_FAVORITE_FUNNELS,
                    }),
              ]}
              value={favoriteFunnelsIds.includes(funnel.id) ? 1 : 0}
            />
          </span>
        </h3>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState, { funnel }: OwnProps) => {
  const favoriteFunnels = getFavoriteFunnels(state);
  return {
    favoriteFunnelsIds: favoriteFunnels,
    isFunnelUsedInLayouts: getLayoutsForEntity(state)(EntityType.DEAL)
      .flatMap(({ schema }) => schema)
      .some(
        ({ defaultValue, field }) =>
          field === "funnelId" && ((defaultValue ?? []) as Funnel["id"][]).includes(funnel.id)
      ),
    isLastFunnel: getFunnels(state).length <= 1,
    reachedFavoriteFunnelsLimit: favoriteFunnels.length >= MAX_FAVORITE_FUNNELS,
  };
};

const mapDispatchToProps = {
  toggleFavoriteFunnel: toggleFavoriteFunnel.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(FunnelHeader);
